import { Eyebrow, MaskLineView, Reveal, Faq as FaqGroup } from "../components/ui-bits";
import JsonLd from "../components/JsonLd";
import { useLang } from "../i18n/LangContext";

const COPY = {
  en: {
    eyebrow: "Information",
    h: ["Frequently asked", "questions."],
    intro:
      "Everything developers, investors and brokers most often ask us. Anything missing — call us, message us on WhatsApp, or use the contact form.",
    groups: [
      {
        title: "For developers",
        items: [
          { q: "What does Verde Capital finance?", a: "Short-term, senior-secured financing for experienced residential developers in Portugal — from land acquisition, to licensing, to pre-sale bridge loans. Typical facilities are €1–5m." },
          { q: "How quickly will I get indicative terms?", a: "Describe your project to Verde, our AI underwriter, and we will send you indicative terms within 24 hours." },
          { q: "What are the terms?", a: "6–24 months, senior-secured, with repayment through bank refinancing, sale or pre-sales." },
          { q: "Which living categories do you finance?", a: "Apartments and homes for sale, build to rent, student housing, senior housing, aparthotels, co-living, serviced apartments and hotels." },
        ],
      },
      {
        title: "For investors",
        items: [
          { q: "What yield do investors receive?", a: "An 8% annual yield, paid quarterly directly to your bank account." },
          { q: "How is my capital secured?", a: "Every loan is senior-secured by a first-ranking charge over the underlying asset — and Verde Capital invests its own capital alongside yours in every facility." },
          { q: "Who can invest?", a: "Professional and qualified investors — private individuals, family offices and companies — from a €100,000 ticket, subject to onboarding and suitability checks." },
        ],
      },
      {
        title: "For brokers",
        items: [
          { q: "How does the broker relationship work?", a: "Introduce qualifying residential development opportunities and Verde does the rest — underwriting, structuring and funding. We pay you 1% of the funded amount, rising to 1.2% on your second funded deal and 1.5% from your third — and the developer is never charged." },
        ],
      },
      {
        title: "General",
        items: [
          { q: "Is Verde Capital a bank?", a: "No. Verde Capital is a subsidiary of Verde 2050, an independent real estate investment platform. Loans are made by Verde Partner Capital B.V. and affiliated lending entities. Nothing on this website is an offer of credit." },
        ],
      },
    ],
    note: "All lending is subject to underwriting, security and full due diligence. Nothing on this page is an offer of credit or investment advice.",
  },
  pt: {
    eyebrow: "Informação",
    h: ["Perguntas", "frequentes."],
    intro:
      "Tudo o que promotores, investidores e intermediários mais nos perguntam. Se falta algo — ligue-nos, envie WhatsApp ou use o formulário de contacto.",
    groups: [
      {
        title: "Para promotores",
        items: [
          { q: "O que financia a Verde Capital?", a: "Financiamento de curto prazo com garantia sénior para promotores residenciais experientes em Portugal — da aquisição de terreno, ao licenciamento, até créditos de ponte para pré-vendas. Os montantes típicos são de €1–5m." },
          { q: "Em quanto tempo recebo condições indicativas?", a: "Descreva o seu projeto à Verde, a nossa subscritora de IA, e enviaremos condições indicativas em 24 horas." },
          { q: "Quais são as condições?", a: "6–24 meses, com garantia sénior, e reembolso através de refinanciamento bancário, venda ou pré-vendas." },
          { q: "Que categorias de habitação financiam?", a: "Apartamentos e moradias para venda, build to rent, habitação estudantil, habitação sénior, aparthotéis, co-living, apartamentos de serviços e hotéis." },
        ],
      },
      {
        title: "Para investidores",
        items: [
          { q: "Que rentabilidade recebem os investidores?", a: "Uma rentabilidade anual de 8%, paga trimestralmente diretamente para a sua conta bancária." },
          { q: "Como está garantido o meu capital?", a: "Cada crédito tem garantia sénior através de um encargo de primeiro grau sobre o ativo subjacente — e a Verde Capital investe o seu próprio capital juntamente com o seu em cada operação." },
          { q: "Quem pode investir?", a: "Investidores profissionais e qualificados — particulares, family offices e empresas — a partir de €100.000, sujeito a onboarding e verificação de adequação." },
        ],
      },
      {
        title: "Para intermediários",
        items: [
          { q: "Como funciona a relação com intermediários?", a: "Apresente oportunidades qualificadas de desenvolvimento residencial e a Verde trata do resto — subscrição, estruturação e financiamento. Pagamos-lhe 1% do montante financiado, a subir para 1,2% no segundo negócio financiado e 1,5% a partir do terceiro — e o promotor nunca paga." },
        ],
      },
      {
        title: "Geral",
        items: [
          { q: "A Verde Capital é um banco?", a: "Não. A Verde Capital é uma subsidiária da Verde 2050, uma plataforma independente de investimento imobiliário. Os créditos são concedidos pela Verde Partner Capital B.V. e entidades de crédito associadas. Nada neste website constitui uma oferta de crédito." },
        ],
      },
    ],
    note: "Todo o crédito está sujeito a subscrição, garantias e diligência prévia. Nada nesta página constitui uma oferta de crédito ou aconselhamento de investimento.",
  },
  nl: {
    eyebrow: "Informatie",
    h: ["Veelgestelde", "vragen."],
    intro:
      "Alles wat ontwikkelaars, investeerders en tussenpersonen ons het vaakst vragen. Ontbreekt er iets — bel, app of gebruik het contactformulier.",
    groups: [
      {
        title: "Voor ontwikkelaars",
        items: [
          { q: "Wat financiert Verde Capital?", a: "Kortlopende financiering met senior zekerheid voor ervaren woningbouwontwikkelaars in Portugal — van grondverwerving en vergunningen tot brugleningen voor voorverkoop. Typische bedragen zijn €1–5 mln." },
          { q: "Hoe snel ontvang ik indicatieve voorwaarden?", a: "Beschrijf uw project aan Verde, onze AI-underwriter, en wij sturen binnen 24 uur indicatieve voorwaarden." },
          { q: "Wat zijn de voorwaarden?", a: "6–24 maanden, met senior zekerheid, en aflossing via bankherfinanciering, verkoop of voorverkoop." },
          { q: "Welke wooncategorieën financieren jullie?", a: "Appartementen en huizen voor verkoop, build to rent, studentenhuisvesting, seniorenwoningen, aparthotels, co-living, serviced apartments en hotels." },
        ],
      },
      {
        title: "Voor investeerders",
        items: [
          { q: "Welk rendement ontvangen investeerders?", a: "Een jaarrendement van 8%, per kwartaal rechtstreeks op uw bankrekening uitgekeerd." },
          { q: "Hoe is mijn kapitaal gedekt?", a: "Elke lening heeft senior zekerheid via een eerste rangs recht op het onderliggende actief — en Verde Capital belegt eigen kapitaal mee in elke transactie." },
          { q: "Wie kan er investeren?", a: "Professionele en gekwalificeerde beleggers — particulieren, family offices en bedrijven — vanaf €100.000, onder voorbehoud van onboarding en geschiktheidstoets." },
        ],
      },
      {
        title: "Voor tussenpersonen",
        items: [
          { q: "Hoe werkt de samenwerking met tussenpersonen?", a: "Dien gekwalificeerde woningbouwontwikkelingen in en Verde regelt de rest — beoordeling, structurering en financiering. Wij betalen u 1% van het gefinancierde bedrag, oplopend naar 1,2% bij uw tweede gefinancierde deal en 1,5% vanaf uw derde — en de ontwikkelaar betaalt nooit." },
        ],
      },
      {
        title: "Algemeen",
        items: [
          { q: "Is Verde Capital een bank?", a: "Nee. Verde Capital is een dochteronderneming van Verde 2050, een onafhankelijk vastgoedbeleggingsplatform. Leningen worden verstrekt door Verde Partner Capital B.V. en gelieerde kredietverlenende entiteiten. Niets op deze website is een kredietaanbod." },
        ],
      },
    ],
    note: "Alle kredietverlening is onder voorbehoud van kredietbeoordeling, zekerheden en due diligence. Niets op deze pagina is een kredietaanbod of beleggingsadvies.",
  },
};

export default function Faq() {
  const { lang } = useLang();
  const c = COPY[lang];
  return (
    <div className="bg-cream">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: c.groups.flatMap((g) => g.items.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        }))),
      }} />
      <section className="pt-32 sm:pt-40 pb-14">
        <div className="container-x max-w-3xl">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h1 className="display mt-6 text-[2.6rem] sm:text-5xl lg:text-[56px] text-charcoal" data-testid="faq-page-headline">
            <MaskLineView>{c.h[0]}</MaskLineView>
            <MaskLineView delay={0.12}>{c.h[1]}</MaskLineView>
          </h1>
          <Reveal delay={0.25} className="mt-7 max-w-2xl">
            <p className="text-lg leading-relaxed text-ink">
              {c.intro}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-20 lg:pb-28">
        <div className="container-x max-w-3xl space-y-14">
          {c.groups.map((g, gi) => (
            <Reveal key={g.title} delay={0.05 + gi * 0.05}>
              <div data-testid={`faq-group-${gi}`}>
                <h2 className="font-mono text-[11px] font-bold uppercase tracking-[0.22em] text-verde-700">
                  {g.title}
                </h2>
                <div className="mt-5">
                  <FaqGroup items={g.items} />
                </div>
              </div>
            </Reveal>
          ))}
          <p className="border-t border-sand pt-6 text-xs leading-relaxed text-fog" data-testid="faq-footer-note">
            {c.note}
          </p>
        </div>
      </section>
    </div>
  );
}
